'use client';

import { FC, useEffect, useState } from 'react';

import { Price } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItem, ShoppingCartProblem } from '@ravago/shared/page-data/models/features';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { ShoppingCartRemoveItemDialogElement } from './shopping-cart-remove-item-dialog-element';

interface Props {
   shoppingCartItem: ShoppingCartItem;
   removeFromShoppingCart: (shoppingCartItemId: ShoppingCartItem['id']) => void;
   getSlugByProductId: (productId: number) => Promise<string | undefined>;
   languageCode: string;
}

export const ShoppingCartItemElement: FC<Props> = ({ shoppingCartItem, removeFromShoppingCart, getSlugByProductId, languageCode }) => {
   const [dialogOpen, setDialogOpen] = useState(false);
   const translate = useTranslations();
   const format = useFormatter();
   const [slug, setSlug] = useState<string | undefined>(undefined);

   useEffect(() => {
      getSlugByProductId(shoppingCartItem.product.baseProductId).then((productSlug) => {
         setSlug(productSlug ?? '');
      });
   }, []);

   type ProblemLevel = 'ERROR' | 'WARNING' | 'INFORMATION';
   type AlertVariant = 'error' | 'warning' | 'info';

   const variantMapping: Record<ProblemLevel, AlertVariant> = {
      ERROR: 'error',
      WARNING: 'warning',
      INFORMATION: 'info'
   };

   const totalPrice: Price = {
      amount: shoppingCartItem.unitPrice.price.amount * shoppingCartItem.quantity.amount,
      currency: shoppingCartItem.unitPrice.price.currency
   };

   const problems = shoppingCartItem.problems.map((problem: ShoppingCartProblem) => (
      <Alert
         headline={problem.level.charAt(0) + problem.level.substring(1, problem.level.length).toLowerCase()}
         key={problem.code}
         variant={variantMapping[problem.level]}
      >
         {translate(problem.code)}
      </Alert>
   ));
   return (
      <Paper data-testid="shopping-cart__shopping-cart-item" shadow>
         <Box direction="column" gap="xs">
            <Box items="center" justify="between">
               {slug === undefined ? (
                  <Typography variant="body-highlight" color="subtle">
                     {shoppingCartItem.product.description}
                  </Typography>
               ) : (
                  <Link disabled={!slug} underline={!slug ? 'none' : 'always'} href={!slug ? '' : `/${languageCode}/products/${slug}`}>
                     <Typography variant="body-highlight" color={!slug ? 'subtle' : 'inherit'}>
                        {shoppingCartItem.product.description}
                     </Typography>
                  </Link>
               )}

               <Typography align="right">
                  <IconButton onClick={() => setDialogOpen(true)} icon="trash" />
               </Typography>
            </Box>

            <Box justify="between" items="end">
               <Box gap="xs" direction="column">
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.quantity')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {format.quantity(shoppingCartItem.quantity)}
                     </Typography>
                  </Box>
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.packaging')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {shoppingCartItem.packaging}
                     </Typography>
                  </Box>
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.product-reference')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {shoppingCartItem.customerProductReference}
                     </Typography>
                  </Box>
               </Box>
               <Box justify="between">
                  <Box direction="column" gap="none">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.unit-price')}:
                     </Typography>
                     <Typography data-testid="shopping-cart__unit-price" color="primary" component="h4" variant="body-highlight">
                        {format.unitPrice(shoppingCartItem.unitPrice)}
                     </Typography>
                  </Box>
                  <Box direction="column" gap="none">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.total-price')}:
                     </Typography>
                     <Typography data-testid="shopping-cart__total-price" color="primary" component="h4" variant="body-highlight">
                        {format.unitPrice({
                           unitOfMeasure: shoppingCartItem.unitPrice.unitOfMeasure,
                           price: {
                              amount: totalPrice.amount,
                              currency: totalPrice.currency
                           }
                        })}
                     </Typography>
                  </Box>
               </Box>
            </Box>

            {problems}
         </Box>
         {dialogOpen && (
            <ShoppingCartRemoveItemDialogElement
               shoppingCartItem={shoppingCartItem}
               removeFromShoppingCart={removeFromShoppingCart}
               onDialogClose={() => setDialogOpen(false)}
            />
         )}
      </Paper>
   );
};
